import { Avatar, AvatarImage, AvatarFallback, AvatarFallbackProps } from "../avatar";
import { cn } from "../../lib/utils";

export interface AvatarItem
  extends Pick<AvatarFallbackProps, "useDeterministicColor" | "deterministicColorMode" | "useStaticBackground"> {
  id: string;
  name: string;
  avatar?: string;
  initials: string;
}

export interface AvatarGroupProps {
  /**
   * Array of avatar items to display
   */
  items: AvatarItem[];
  /**
   * Maximum number of avatars to show before showing "+N"
   * @default 3
   */
  maxVisible?: number;
  /**
   * Size of each avatar
   * @default "md" (40px)
   */
  size?: "sm" | "md" | "lg";
  /**
   * Additional CSS classes for the container
   */
  className?: string;
  /**
   * Show loading state
   */
  isLoading?: boolean;
  /**
   * Fallback content when no items and not loading
   * @default "?"
   */
  emptyFallback?: string;
}

const sizeClasses = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-12 w-12",
};

const overlapOffsets = {
  sm: 16, // pixels
  md: 20, // pixels
  lg: 26, // pixels
};

/**
 * AvatarGroup - Display multiple avatars in an overlapping group
 *
 * Features:
 * - Overlapping avatars for multiple items
 * - Shows "+N" indicator for items beyond maxVisible
 * - Supports loading and empty states
 * - Configurable size (sm, md, lg)
 * - Proper z-index stacking
 *
 * @example
 * ```tsx
 * <AvatarGroup
 *   items={[
 *     { id: "1", name: "John Doe", avatar: "...", initials: "JD" },
 *     { id: "2", name: "Jane Smith", initials: "JS" }
 *   ]}
 *   maxVisible={3}
 *   size="md"
 * />
 * ```
 */
export function AvatarGroup({
  items,
  maxVisible = 3,
  size = "md",
  className,
  isLoading = false,
  emptyFallback = "?",
}: AvatarGroupProps) {
  const sizeClass = sizeClasses[size];
  const overlapOffset = overlapOffsets[size];

  // Loading state
  if (isLoading) {
    return (
      <div className={cn("relative flex items-center", className)} style={{ width: sizeClasses[size] }}>
        <Avatar className={sizeClass}>
          <AvatarFallback>...</AvatarFallback>
        </Avatar>
      </div>
    );
  }

  // Empty state
  if (items.length === 0) {
    return (
      <div className={cn("relative flex items-center", className)} style={{ width: sizeClasses[size] }}>
        <Avatar className={sizeClass}>
          <AvatarFallback>{emptyFallback}</AvatarFallback>
        </Avatar>
      </div>
    );
  }

  // Single avatar
  if (items.length === 1 && items[0]) {
    const item = items[0];
    return (
      <div className={cn("relative flex items-center", className)} style={{ width: sizeClasses[size] }}>
        <Avatar className={sizeClass}>
          <AvatarImage src={item.avatar} alt={item.name} />
          <AvatarFallback
            useDeterministicColor={item.useDeterministicColor}
            useStaticBackground={item.useStaticBackground}
            deterministicColorMode={item.deterministicColorMode}
            id={item.id}
          >
            {item.initials}
          </AvatarFallback>
        </Avatar>
      </div>
    );
  }

  // Multiple avatars
  const visibleItems = items.slice(0, maxVisible);
  const remainingCount = Math.max(0, items.length - maxVisible);
  const containerWidth = visibleItems.length > 1 ? overlapOffset * (visibleItems.length - 1) + 40 : 40;

  return (
    <div className={cn("relative flex items-center", className)} style={{ width: `${containerWidth}px` }}>
      {visibleItems.map(({ useDeterministicColor, deterministicColorMode, useStaticBackground, ...item }, index) => (
        <Avatar
          key={item.id}
          className={cn(sizeClass, "border-2 border-background")}
          style={{
            position: index === 0 ? "relative" : "absolute",
            left: index === 0 ? 0 : `${index * overlapOffset}px`,
            zIndex: maxVisible - index + 1,
          }}
        >
          <AvatarImage src={item.avatar} alt={item.name} />
          <AvatarFallback
            useDeterministicColor={useDeterministicColor}
            useStaticBackground={useStaticBackground}
            deterministicColorMode={deterministicColorMode}
            id={item.id}
          >
            {item.initials}
          </AvatarFallback>
        </Avatar>
      ))}

      {/* Show "+N" indicator if more items than maxVisible */}
      {remainingCount > 0 && (
        <Avatar
          className={cn(sizeClass, "border-2 border-background bg-muted")}
          style={{
            position: "absolute",
            left: `${visibleItems.length * overlapOffset}px`,
            zIndex: 0,
          }}
        >
          <AvatarFallback className="text-xs font-semibold group-hover:bg-white">+{remainingCount}</AvatarFallback>
        </Avatar>
      )}
    </div>
  );
}
